import { Injectable } from '@angular/core';
import {
  ToastController,
  ModalController,
  LoadingController,
} from 'ionic-angular';
import { App as AppPlugin, AppState } from '@capacitor/app';
import 'rxjs/add/operator/map';
import { CrudApiProvider } from '../crud-api/crud-api';
import { EventEmitter } from '../../includes/eventEmitter';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ContentToolsProvider } from '../content-tools/content-tools';
import { SettingsProvider } from '../settings/settings';

declare let CmKit: any;

@Injectable()
export class UserProvider extends EventEmitter {
  // Static so all instances of the provider use the same object
  static staticInfo: any;

  log = debug('app:UserProvider');

  authenticated = false;
  unauthedUser = false;
  hasFriends = false;

  unauthedPermissions = {
    // pages
    maps: false,
    friends: false,
    jobs: true,
    settings: false,
    notifications: false,
    profile: false,
    // engagementTypes
    post: false,
    comment: false,
    like: true,
    vote: true,
    poll: true,
    bookmark: false,

    // pages
    JobsPage: true,
    ShareAppPage: true,
    WreckersFeedPage: true,
    StatusSettingsPage: true,
  };

  subs: any = [];

  info: any = {};
  loadingContactNotifications = false;
  pendingContactNotifications: any = {};
  clearingPendingContactNotifications = false;
  tools: any;
  applications: any = {};
  jobSortOption = 'score.random:desc';
  unauthedClearJobFilterInit = false;
  jobFilterDetails: any = {
    trailerType: {
      dryVan: false,
      reefer: false,
      hauler: false,
      tanker: false,
      doubtrip: false,
      flatbed: false,
      hhg: false,
      hazmat: false,
      specialized: false,
      intermodal: false,
      doublepups: false,
      dryBulk: false,
      other: false,
    },

    driverType: {
      soloLease: false,
      soloOwner: false,
      soloCompany: false,
      teamLease: false,
      teamOwner: false,
      studentRecentGrad: false,
      teamCompany: false,
    },

    jobType: {
      local: true,
      regional: true,
      otr: true,
      dedicatedlane: true,
      dedicatedaccount: true,
    },
  };
  myFriendsPromise;

  private isApplying = false;

  constructor(
    public crudApi: CrudApiProvider,
    public toastCtrl: ToastController,
    public modalCtrl: ModalController,
    public loadingCtrl: LoadingController,
    public contentTools: ContentToolsProvider,
    public settings: SettingsProvider
  ) {
    super();
    this.contentTools.user = this;
    this.settings.user = this;
    this.info = UserProvider.staticInfo;

    // handles call from a cold start, there is another call in the listener
    // below that handles returning to the foreground
    setTimeout(() => {
      this.getPendingContactAlerts();
    }, 2000);

    AppPlugin.addListener('appStateChange', (state: AppState) => {
      if (_.get(state, 'isActive') === true) {
        if (this.get('_id')) {
          this.crudApi.get('/api/users/me').then(
            res => {
              this.emit('beforeUserUpdateOnFocus');
              this.updateUser(res.data);
              this.log('getWeather | user focus update', res.data.recentStatus);
              this.tools.getCurrentWeather();
              this.emit('userUpdateOnFocus');
            },
            () => {}
          );

          if (window['cordova']) {
            this.getPendingContactAlerts();
          }
        }
      }
    });
  }

  private getFBCustomEventString(type: String) {
    let trackFailure = '';
    let trackSuccess = '';
    switch (type) {
      case 'add friends':
        trackFailure = 'attempt-friend-verify-phone-failure';
        trackSuccess = 'attempt-friend-verify-phone-success';
        break;
      case 'make a comment':
        trackFailure = 'attempt-comment-verify-phone-failure';
        trackSuccess = 'attempt-comment-verify-phone-success';
        break;
      case 'create a comment':
        trackFailure = 'attempt-post-verify-phone-failure';
        trackSuccess = 'attempt-post-verify-phone-success';
        break;
      default:
        trackFailure = 'attempt-verify-phone-failure';
        trackSuccess = 'attempt-verify-phone-success';
        break;
    }
    return { trackFailure, trackSuccess };
  }

  userPermission(value) {
    if (this.authenticated && !this.unauthedUser) {
      return true;
    } else {
      return this.unauthedPermissions[value];
    }
  }

  userAccountAge() {
    return moment().diff(this.info?.dates?.created, 'days');
  }

  updateUser(u?) {
    this.log('updated user provider', u || this.info);
    const accountTier = _.get(this, 'info.accountTier');
    UserProvider.staticInfo = this.info = window['$user'] = u || this.info;
    _.set(this.info, 'accountTier', accountTier);
    this.checkProfileCompletion();
    this.emit('updated');
    this.updateApplications();
  }

  mergeUser(u) {
    this.updateUser(_.merge(this.info, u));
    this.updateApplications();
  }

  forceUpdateUser(override = false) {
    return this.crudApi.get('/api/users/me').then(
      res => {
        if (override) {
          this.updateUser(res.data);
        } else {
          this.mergeUser(res.data);
        }
      },
      e => {}
    );
  }

  isAdmin() {
    return _.get(this.info, 'role') === 'admin';
  }

  // Deprecated - only admins really
  isModerator() {
    return _.get(this.info, 'role') === 'moderator' || this.isAdmin();
  }

  isContentModerator() {
    return _.get(this.info, 'subRole') === 'contentModerator' || this.isAdmin();
  }

  // get a user value
  // get(k: String) {
  // keesp causing errors: Argument of type 'String' is not assignable to parameter of type 'Many<PropertyKey>'. Type 'String' is not assignable to type 'PropertyKey[]'. Property 'push' is missing in type 'String'.
  get(k?: any) {
    if (k === undefined) {
      return this.info;
    }
    return _.get(this.info, k);
  }

  set(k: any, v: any) {
    return _.set(this.info, k, v);
  }

  getJobSortOption() {
    return this.jobSortOption;
  }

  setJobSortOption(d) {
    this.jobSortOption = d;
  }

  getJobFilterDetails() {
    return this.jobFilterDetails;
  }

  setJobFilterDetails(d) {
    this.jobFilterDetails = d;
  }

  hasAppliedToCompany(id) {
    return !!this.applications[id];
  }

  getUserTag(info) {
    let tempUserTag = '';

    if (info.cdlLicense) {
      tempUserTag = 'cdlHolder';
    }

    if (info.companyOrOwner === 'ownerOperator') {
      tempUserTag = 'ownerOperator';
    }

    if (
      info.companyOrOwner === 'ownerOperator' &&
      info.powerUnitOrOwnAuthority === 'own-authority'
    ) {
      tempUserTag = 'ownAuthority';
    }
    return tempUserTag;
  }

  setJobFilterProfileDefaults() {
    let defaults = _.get(this.info, 'jobSearchInfo') || {};

    // RESETTING FILTER DETAILS
    _.each(_.get(this.jobFilterDetails, 'trailerType'), (t, k) => {
      this.jobFilterDetails.trailerType[k] = false;
    });
    _.each(_.get(this.jobFilterDetails, 'driverType'), (t, k) => {
      this.jobFilterDetails.driverType[k] = false;
    });
    _.each(_.get(this.jobFilterDetails, 'jobType'), (t, k) => {
      this.jobFilterDetails.jobType[k] = false;
    });

    if (defaults.companyOrOwner === 'companyDriver') {
      if (defaults.soloOrTeam === 'soloDriver') {
        this.jobFilterDetails.driverType.soloCompany = true;
      } else if (defaults.soloOrTeam === 'teamDriver') {
        this.jobFilterDetails.driverType.teamCompany = true;
      } else {
        // assume solo
        this.jobFilterDetails.driverType.soloCompany = true;
      }
    } else if (defaults.companyOrOwner === 'ownerOperator') {
      if (defaults.soloOrTeam === 'soloDriver') {
        this.jobFilterDetails.driverType.soloOwner = true;
      } else if (defaults.soloOrTeam === 'teamDriver') {
        this.jobFilterDetails.driverType.teamOwner = true;
      } else {
        // assume solo
        this.jobFilterDetails.driverType.soloOwner = true;
      }
    } else if (defaults.companyOrOwner === 'leasePurchase') {
      if (defaults.soloOrTeam === 'teamDriver') {
        this.jobFilterDetails.driverType.teamLease = true;
      } else {
        // assume solo
        this.jobFilterDetails.driverType.soloLease = true;
      }
    } else if (!defaults.companyOrOwner) {
      if (defaults.soloOrTeam === 'soloDriver') {
        this.jobFilterDetails.driverType.soloCompany = true;
      } else if (defaults.soloOrTeam === 'teamDriver') {
        this.jobFilterDetails.driverType.teamCompany = true;
      }
    }

    if (defaults.experience) {
      this.jobFilterDetails.experience = defaults.experience;
    }
  }

  getFilteredJobQuery(opts?) {
    opts = _.merge(
      {
        profile: true,
        filters: true,
      },
      opts
    );

    let queryOptions: any = {
      trailerType: [],
      driverType: [],
      jobType: [],
    };

    if (opts.profile) {
      _.each(this.jobFilterDetails.driverType, (t, k) => {
        if (t) {
          queryOptions.driverType.push(k);
        }
      });
      queryOptions.experience = this.jobFilterDetails.experience;
    }

    if (opts.filters) {
      _.each(this.jobFilterDetails.trailerType, (t, k) => {
        if (t) {
          queryOptions.trailerType.push(k);
        }
      });
      _.each(this.jobFilterDetails.jobType, (t, k) => {
        if (t) {
          queryOptions.jobType.push(k);
        }
      });
    }

    let q = this.filteredJobDetailsToQueryOptions(queryOptions);

    this.log('filtered query', q);
    return q;
  }

  saveTripToUser(trip) {
    this.log('saving trip to user', trip);
    _.set(this, 'info.currentTrip', trip);
    return this.crudUpdateTrip({ currentTrip: trip });
  }

  crudUpdateTrip(trip?) {
    let update = trip;

    if (_.size(update.currentTrip) === 0) {
      this.log('NOT ROUTE trip null');
    } else {
      if (update.currentTrip.dates) {
        this.log('UPDATING trip', update);
        update.currentTrip.dates.updated = moment();
      } else if (!update.currentTrip.dates) {
        this.log('CREATING Trip', update);
        update.currentTrip.dates = {
          updated: moment(),
          created: moment(),
        };
      }
    }

    this.log('will save trip');

    return this.crudApi
      .put(`/api/users/${this.get('_id')}/currentTrip`, update)
      .then(
        res => {
          this.log('trip saved to user');
          this.updateUser();
          this.emit('tripSaved');
        },
        e => {
          console.warn('error on current trip save');
        }
      );
  }

  filteredJobDetailsToQueryOptions(q?) {
    let queryOptions = q || {},
      zip = this.get('jobSearchInfo.zip'),
      query: any = {
        postType: 'JOB',
        zipcode: zip,
        'jobInformation.job.trailerType': { $in: queryOptions.trailerType },
        'jobInformation.job.driverType': { $in: queryOptions.driverType },
        'jobInformation.job.jobTypeSelect': { $in: queryOptions.jobType },
      };

    if (queryOptions.experience) {
      query['jobInformation.job.experience'] = queryOptions.experience;
    }

    _.each(query, (v, k) => {
      if (v && v.$in && !_.size(v.$in)) {
        delete query[k];
      }
    });

    return query;
  }

  crudUpdate(d, opts?) {
    opts = opts || {};
    const loader = this.loadingCtrl.create();
    this.log('user', d, this.get('_id'));
    if (!opts.hideLoader) {
      loader.present();
    }

    return this.crudApi.put(`/api/users/${this.get('_id')}`, d).then(
      res => {
        this.updateUser(res.data);
        if (!opts.hideLoader) {
          return loader.dismiss();
        }
      },
      e => {
        console.warn('an error occured: ', e);
        if (!opts.hideLoader) {
          loader.dismiss();
        }
        return Promise.reject(e);
      }
    );
  }

  getJobApplications() {
    return _.filter(this.get('jobApplications'), d => {
      return moment().subtract(30, 'days').toDate() < new Date(d.date);
    });
  }

  filterAppliedCompanies() {
    // Filter out all applications more than 30 days ago
    const appliedCompanies = _.map(this.getJobApplications(), d => {
      return d.poster;
    });

    return {
      excludeApplied: true,
    };
  }

  checkProfileCompletion() {
    let u: any = _.get(this, 'info'),
      profileCompleted = false;

    this.log('checking account info completion', u);
    if (
      _.get(u, 'firstName') &&
      _.get(u, 'lastName') &&
      _.get(u.jobSearchInfo, 'dob') &&
      _.get(u.jobSearchInfo, 'phone') &&
      _.get(u.jobSearchInfo, 'zip') &&
      _.get(u.jobSearchInfo, 'experience') &&
      _.get(u.jobSearchInfo, 'cdlLicense')
    ) {
      this.log('checking account info - PROFILE COMPLETE');
      // if we make it here the profile is all filled out
      profileCompleted = true;
    }

    if (
      profileCompleted &&
      !_.get(this.info, 'stats.events.completedProfile')
    ) {
      this.log('checked account info', profileCompleted);
      _.set(this.info, 'stats.events.completedProfile', 1);
      this.tools.logEventAndFlush('completedProfile');
    }

    return profileCompleted;
  }

  applyToJob(job, poster, navCtrl, opts: any = {}) {
    if (this.isApplying) {
      //never resolve
      const fn: any = () => {};
      return new Promise(fn);
    }

    this.log('starting applyToJob', job, poster);

    this.isApplying = true;

    return Promise.resolve().then(() => {
      return new Promise((resolve, reject) => {
        if (!this.userPermission('jobs')) {
          this.isApplying = false;
          return Promise.resolve(false);
        }

        this.log('applications', this.get('jobApplications'));

        if (this.hasAppliedToCompany(_.get(poster, '_id'))) {
          this.log('has applied already');
          this.presentToast({
            message: 'You have already applied to this company.',
            duration: 2500,
          });
          this.isApplying = false;
          return Promise.resolve(false);
        }

        if (
          localStorage.getItem('CDLLIFE_APPLICATION_SUBMITTED') === 'false' ||
          this.unauthedUser
        ) {
          const jobSearchModal = this.modalCtrl.create(
            'JobSearchInfoModalPage',
            {
              saveButtonText: 'Apply Now',
              submittingApplication: true,
              job: job,
            }
          );
          jobSearchModal.present();
          jobSearchModal.onDidDismiss(opts => {
            if (!_.get(opts, 'skipSearch') && opts) {
              submitApplication(this);
              this.isApplying = false;
              resolve(true);
            } else {
              // cancelled
              this.isApplying = false;
              resolve(false);
            }
          });
        } else {
          submitApplication(this);
          this.isApplying = false;
          resolve(true);
        }

        function submitApplication(self) {
          try {
            self.info.jobApplications = self.info.jobApplications || [];
            self.info.jobApplications.push({
              job: _.get(job, '_id'),
              poster: _.get(poster, '_id'),
              date: new Date().toISOString(),
            });
          } catch (e) {
            console.warn('ERROR', e);
          }

          self.appliedMessage(job);

          return self.tools
            .recaptchaCheck('application')
            .then(data => {
              return self.crudApi
                .post('/api/applications', {
                  applicationInfo: self.get('jobSearchInfo'),
                  job: _.get(job, '_id'),
                  poster: _.get(poster, '_id'),
                  security: data,
                  tr: self.tools.getTrackingParams(),
                })
                .then(res => {
                  self.log('application created', res.data);
                  self.forceUpdateUser();
                  self.emit('appliedForJob', job, poster);
                  try {
                    self.tools.logAnalyticsEvent(
                      'trackCustom',
                      'apply-job',
                      _.merge(
                        {
                          poster: poster,
                        },
                        opts
                      )
                    );
                    self.tools.logAnalyticsEvent('track', 'SubmitApplication');
                  } catch (e) {}
                  self.tools.logEventAndFlush('jobApplication');
                  self.updateApplications();
                  localStorage.setItem('CDLLIFE_APPLICATION_SUBMITTED', 'true');
                  return resolve(true);
                })
                .catch(e => {
                  self.log('error', e);
                  self.presentToast({
                    message: 'A duplicate application was found',
                    duration: 2500,
                  });
                  return resolve(false);
                });
            })
            .then(
              d => {
                self.isApplying = false;
                return Promise.resolve();
              },
              e => {
                self.isApplying = false;
                return Promise.reject(e);
              }
            );
        }
      });
    });
  }

  applyAllJobs(query, navCtrl) {
    this.log('apply all query', query);
    const loader = this.loadingCtrl.create();
    loader.present();
    if (!this.checkProfileCompletion()) {
      this.log('has not filled out job search');
      this.jobFormToast(navCtrl);
      loader.dismiss();
      return Promise.resolve(false);
    }

    return this.tools.recaptchaCheck('application').then(data => {
      return this.crudApi
        .post(this.crudApi.addQuery('/api/applications/applyAll', query), {
          applicationInfo: this.get('jobSearchInfo'),
        })
        .then(res => {
          this.updateUser();
          this.forceUpdateUser();
          this.log('applications applied', res.data);
          loader.dismiss();
          this.tools.logEventAndFlush('jobApplicationsAll');
          this.tools.logEventAndFlush(
            'jobApplication',
            'jobApplication',
            {},
            {
              count: _.size(_.get(res.data, 'jobs')),
            }
          );
        })
        .catch(e => {
          this.log('error:', e);
        });
    });
  }

  userShouldSeeContent(content) {
    const uid = _.get(content, 'user._id') || _.get(content, 'user');

    // always see your own stuff

    if (uid === this.get('_id')) {
      return true;
    }

    const banned =
      _.get(content, 'states.isBanned') ||
      _.get(content, 'states.isShadowBanned') ||
      _.get(content, 'states.hasBeenAutoRemoved');

    // otherwise, never see banned
    if (banned) {
      this.log('HIDING CONTENT: banned', content._id);
      return false;
    }

    const hiddenPosts = this.get('hiddenPosts') || [],
      blockedUsers = _.union(
        this.get('blockedUsers'),
        this.get('blockedByUsers')
      );

    // if you hide it
    if (_.includes(hiddenPosts, content._id)) {
      this.log('HIDING CONTENT: hidden post', content._id);
      return false;
    }

    // if you can't see this users stuff
    if (_.includes(blockedUsers, uid)) {
      this.log('HIDING CONTENT: blocked user', uid);
      return false;
    }

    const vSetting = this.contentTools.getVisibilitySettingForContent(content);
    if (vSetting === 'HIDE') return false;

    return true;
  }

  reloadFriends() {
    this.myFriendsPromise = null;
    this.myFriends();
  }

  myFriends(q?) {
    if (this.myFriendsPromise && !q) {
      return this.myFriendsPromise;
    }

    return (this.myFriendsPromise = this.crudApi
      .get('/api/friends-simple', q)
      .then(
        res => {
          this.hasFriends = _.size(_.get(res, 'data')) ? true : false;
          return _.keyBy(res && res.data, 'userId');
        },
        e => {
          return [];
        }
      ));
  }

  private presentToast(opts) {
    let toast = this.toastCtrl.create(
      _.merge(
        {
          message: '',
          duration: 2500,
          showCloseButton: false,
          closeButtonText: 'Close',
        },
        opts
      )
    );
    toast.onDidDismiss(() => {});
    toast.present();
  }

  private jobFormToast(navCtrl) {
    let t = this.toastCtrl.create({
        message: 'You must fill out the job search information to apply',
        showCloseButton: true,
        closeButtonText: 'Complete Form',
      }),
      closedByTimeout = false,
      timeoutHandle = setTimeout(() => {
        closedByTimeout = true;
        t.dismiss();
      }, 5000);

    t.onDidDismiss(() => {
      if (closedByTimeout) return;
      clearTimeout(timeoutHandle);
      navCtrl.push('JobSearchInfoModalPage');
    });

    t.present();
  }

  private appliedMessage(job) {
    CmKit.ActionSheet({
      props: {
        templates: {
          title: () => {
            return `
                   <div class="cm-ListItem cm-Padding--remove-horizontal cm-Margin--bottom" >
                      <div class="cm-ListItem-middle">
                        <div style="text-align:center;">
                        <span><i cm-font="Title / 24 Semibold" class="fal fa-check-circle" cm-color="Accent / Success"></i></span>
                         <span  cm-font="Title / 24 Semibold" >Application Submitted</span>
                        </div>
                         <br/>
                        <div cm-font="Body / 14 Regular" style="text-align:center;" cm-color="Neutral / Mine Shaft">
                          Thanks for applying! A recruiter from <b>${job.companyName}</b> will be reaching out to you to discuss your application and to answer any questions that you may have about the job. In the meantime, check out more great jobs that CDLLife has to offer.
                        </div>
                        <br/>
                      </div>
                    </div>
                 `;
          },
          cancel: `
              <div style="text-align:center"
                    cm-font="Button / 12 Regular"
                    class="cm-Margin--bottom"
                    cm-color="Neutral / Manatee">
                Dismiss
              </div>
              `,
        },
      },
    });
  }

  private updateApplications() {
    this.applications = _.keyBy(this.getJobApplications(), 'poster');
  }

  getDaysSinceJoined() {
    if (!this.get('dates.created')) {
      return 0;
    } else {
      const creationDate = moment(this.get('dates.created'));
      return moment().diff(moment(creationDate), 'days');
    }
  }

  isNewUser(): boolean {
    if (!this.get('dates.created')) {
      return true;
    } else {
      const creationDate = moment(this.get('dates.created'));
      return moment().diff(moment(creationDate), 'minutes') < 5;
    }
  }

  getPendingContactAlerts() {
    const userId = this.get('_id');

    if (
      this.loadingContactNotifications ||
      this.clearingPendingContactNotifications ||
      !userId
    ) {
      return;
    }

    this.loadingContactNotifications = true;

    this.crudApi.get(`/api/users/${userId}/counts/contactNotifications`).then(
      res => {
        this.log('updating pendingContactNotifications', res.data);
        this.pendingContactNotifications = res.data || {};
        setTimeout(() => {
          this.tools.emit('pending-contact-updated');
          this.loadingContactNotifications = false;
        });
      },
      e => {
        this.log('error getting contact notification count', e);
        this.loadingContactNotifications = false;
      }
    );
  }

  hasPendingContactAlerts() {
    let hasPendingNotif = false;

    _.each(this.pendingContactNotifications, v => {
      if (v) {
        hasPendingNotif = true;
        return;
      }
    });

    return hasPendingNotif;
  }

  clearPendingContactAlerts() {
    this.pendingContactNotifications = null;
    this.tools.emit('pending-contact-updated');
  }

  setActivatedUserStatus() {
    if (
      this.get('_id') &&
      !this.get('states.activatedUser') &&
      !moment(this.get('dates.created')).isSame(new Date(), 'day')
    ) {
      this.crudApi.put(`/api/users/${this.get('_id')}/activatedUser`).then(
        res => {
          this.forceUpdateUser();
        },
        e => {
          this.log('error setting activatedUser state', e);
        }
      );
    }
  }

  getPublicInfo() {
    const { _id, username, userNamePretty, userImage } = this.info;
    return {
      _id,
      username,
      userNamePretty,
      userImage,
    };
  }
}



// WEBPACK FOOTER //
// ./src/providers/user/user.ts